import * as React from "react";

import {ApolloRouterContext, type ApolloGqlClient} from "@repo/data-gql-apollo";
import type {GAEModule} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

/**
 * Result of the `useGql` hook.
 */
type Output = {
    /**
     * The current GQLClient instance.
     */
    readonly gqlClient: ApolloGqlClient;
    /**
     * The default GAE module that GQL operations should use.
     */
    readonly gqlDefaultModule: GAEModule;
};

/**
 * Get the current client instance for Apollo operations and default module.
 *
 * @returns {Output} An object containing the current client instance
 * and default GAE module that Apollo-based operations should use.
 */
export const useApolloGqlClient = (): Output => {
    const {client, module} = React.useContext(ApolloRouterContext);
    if (client == null) {
        if (process.env.NODE_ENV === "production") {
            throw new KAError("No GqlRouter", Errors.Internal);
        } else {
            throw new KAError(
                "No GqlRouter defined; did you forget a gql test harness adapter config?",
                Errors.Internal,
            );
        }
    }

    return {
        gqlClient: client,
        gqlDefaultModule: module,
    };
};
